import {useRef} from 'react';
import {useTheme,Box,Autocomplete,TextField,ListItemIcon,Chip} from '@mui/material';
import {DefaultConfig} from '../../../core/utilities/Utilities';
import {createFilterOptions} from '@mui/material/Autocomplete';
import {getIconComponent} from "../../../custom/icons/iconLoader";
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
const readOnlyBackgroundColor=DefaultConfig.readOnlyBackgroundColor;
const filter=createFilterOptions();

const EMPTY_SENTINEL={
    __sentinel:true,
    id:'__sentinel',
    value: "__sentinel",
};

const FieldAutocompleteMultiple=({multivalueFields,fieldName,application,recordRules,setMultivalueFields,readOnly,setFieldChanged})=>{
    const theme=useTheme();
    const primaryColor=theme.palette.primary.main;
    const contrastColor=theme.palette.primary.light;
    const readOnlyEvaluated=readOnly||(recordRules?.can_update_record===false);
    const displayField=multivalueFields[fieldName]?.display_field||'id';
    const iconName=multivalueFields[fieldName]?.icon_component;
    const IconComponent=iconName?getIconComponent(iconName):null;
    const canAddNew=multivalueFields[fieldName]?.can_add_new??false;
    const tempIdCounter=useRef(-1);
    const getNextTempId=()=>tempIdCounter.current--;
    const rawValue=multivalueFields[fieldName]?.related_records??[];
    const safeValue=rawValue.length===0?[EMPTY_SENTINEL]:rawValue;
    return (
        <Box sx={{flex:1,minWidth:0}}>
            <Autocomplete
                style={{marginTop:"0px"}}
                readOnly={readOnlyEvaluated}
                fullWidth
                multiple
                options={multivalueFields[fieldName]?.available_records??[]}
                id={fieldName}
                name={fieldName}
                size="small"
                value={safeValue}
                noOptionsText={application.getLocale("NoRecordFound")}
                onChange={(event,newValue)=>{
                    const cleaned=newValue.filter(v=>!v.__sentinel);
                    setFieldChanged(true);
                    setMultivalueFields({...multivalueFields,[fieldName]:{...multivalueFields[fieldName],related_records:cleaned}});
                }}
                isOptionEqualToValue={(option,value)=>{
                    if (!value||value.__sentinel) return false;
                    return option.id===value.id;
                }}
                filterOptions={(options,params)=>{
                    const filtered=filter(options,params);
                    const {inputValue}=params;
                    if(!canAddNew||!inputValue?.trim()) return filtered;
                    const exists=options.some(o=>o?.[displayField]?.toLowerCase()===inputValue.toLowerCase());
                    if(!exists){
                        filtered.push({
                            id:getNextTempId(),
                            [displayField]:inputValue,
                            display_icon:false,
                            isNew:true,
                        });
                    }
                    return filtered;
                }}
                getOptionLabel={(option)=>{
                    if(typeof option==='string') return option;
                    if(option.inputValue) return option.inputValue;
                    return option[displayField]||'';
                }}
                renderOption={(props,option)=>{
                    const {key, ...rest}=props;
                    if(option.isNew){
                        return (
                            <li key={key} {...rest}>
                                <ListItemIcon sx={{minWidth:0,mr:1}}>
                                    <AddIcon fontSize="small" />
                                </ListItemIcon>
                                <em>“{option[displayField]}”</em>
                            </li>
                        );
                    }
                    return (
                        <li key={key} {...rest}>
                            {option.display_icon&&IconComponent&&(
                                <ListItemIcon sx={{minWidth: 0,mr:1}}>
                                    <IconComponent />
                                </ListItemIcon>
                            )}
                            {option[displayField]}
                        </li>
                    );
                }}
                renderTags={(value=[],getTagProps)=>{
                    return value.map((option,index)=>{
                        const tagProps=getTagProps({index});
                        const {key,...rest}=tagProps;
                        if(option.__sentinel){
                            return (
                                <Chip
                                    key={key}
                                    {...rest}
                                    label="__sentinel__"
                                    icon={IconComponent?(<IconComponent style={{ color: 'white' }}/>):null}
                                    style={{
                                        display:'none',
                                        fontSize:'0.7125rem',
                                        color:'white',
                                        backgroundColor:primaryColor,
                                    }}
                                />
                            );
                        }
                        const isNew=option.isNew===true;
                        return (
                            <Chip
                                key={key}
                                {...rest}
                                sx={{
                                    height: '100%',
                                    "& .MuiChip-deleteIcon":{color:'white'},
                                    "& .MuiChip-deleteIcon:hover":{color:'rgba(255,255,255,0.65)'},
                                }}
                                icon={
                                    isNew?(
                                        <CheckIcon style={{color:'white'}} fontSize="small" />
                                    ):option?.display_icon && IconComponent?(
                                        <IconComponent style={{color:'white'}}/>
                                    ):null
                                }
                                label={option?.[displayField]}
                                style={{
                                    fontSize:'0.7125rem',
                                    color:'white',
                                    backgroundColor:isNew?contrastColor:primaryColor,
                                }}
                            />
                        );
                    });
                }}
                renderInput={(params)=>(
                    <TextField {...params} 
                        sx={{div:{backgroundColor:readOnlyEvaluated?readOnlyBackgroundColor:""}}} 
                        style={{marginTop:"0px",marginBottom:"8px"}}
                        fullWidth
                        variant="outlined"
                        size="small"
                        label={multivalueFields[fieldName]?.ui_label_locale?application.getLocale(multivalueFields[fieldName]?.ui_label_locale):fieldName}
                        margin="normal"/>
                )}
            />
        </Box>
    );
}

export default FieldAutocompleteMultiple;
